import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Card from 'react-bootstrap/Card';
import { Form } from 'react-bootstrap';

import SpinnerButton from '../../../common/components/Button';
import ValidationAlert from '../../../app/components/ValidationAlert';
import FormRow from '../../../app/components/FormRow';
import {
  CreateUpdateVisitRegistrationDto,
  GetAppointmentChannelDto,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import ExamTypeSelector from '../Selects/ExamTypeSelector';
import AppointmentChannelSelector from '../Selects/AppointmentChannelSelector';
import momencik from '../../../common/momencik';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { useSpdCore } from '../../../common/hooks/useSpdCore';

interface ExternalVisitBookingEditorProps {
  id?: string;
  title: string;
  visitRegistration?: CreateUpdateVisitRegistrationDto;
  appointmentChannels?: GetAppointmentChannelDto[];
  onCancel: () => void;
  show?: boolean;
  onSubmit: (visit: CreateUpdateVisitRegistrationDto) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const ExternalVisitBookingEditor: React.FC<
  ExternalVisitBookingEditorProps
> = props => {
  const [visit, setVisit] = useState(props.visitRegistration);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const spd = useSpdCore();

  useEffect(() => {
    setVisit(props.visitRegistration);
    setSaveErrors([]);
    setShowSaveErrors(false);
  }, [props.show, props.visitRegistration]);

  const propertyChange = (obj: Partial<CreateUpdateVisitRegistrationDto>) => {
    setVisit({ ...visit, ...obj });
  };

  const changeAppointmentChannel = (channelId: string) => {
    const channel = props.appointmentChannels?.find(c => c.id === channelId);
    setVisit({
      ...visit,
      appointmentChannelId: channelId,
      visitTime: Number(channel?.timeWindow) || 20,
    });
  };

  const onSubmit = async () => {
    if (!visit) {
      return;
    }
    setIsSaving(true);
    try {
      const saveStatus = await props.onSubmit(visit);
      if (!saveStatus.saved) {
        setSaveErrors(saveStatus.errors);
        setShowSaveErrors(true);
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      onHide={props.onCancel}
      show={props.show}
      centered={true}
      keyboard={true}
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>{props.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Card className="mb-2">
          <Card.Body>
            <Form className="d-grid gap-3">
              <FormRow controlId="firstname" label="Imię">
                <Form.Control
                  value={visit?.firstname || ''}
                  onChange={e => propertyChange({ firstname: e.target.value })}
                />
              </FormRow>
              <FormRow controlId="surname" label="Nazwisko">
                <Form.Control
                  value={visit?.surname || ''}
                  onChange={e => propertyChange({ surname: e.target.value })}
                />
              </FormRow>
              <FormRow controlId="phone" label="Telefon">
                <Form.Control
                  value={visit?.phone || ''}
                  onChange={e => propertyChange({ phone: e.target.value })}
                />
              </FormRow>
              <FormRow controlId="mail" label="E-mail">
                <Form.Control
                  value={visit?.mail || ''}
                  onChange={e => propertyChange({ mail: e.target.value })}
                />
              </FormRow>
              <FormRow controlId="examType" label="Typ badania">
                <ExamTypeSelector
                  value={visit?.examTypeId || ''}
                  onChange={e => propertyChange({ examTypeId: e.value })}
                />
              </FormRow>
              <FormRow
                controlId="appointmentChannel"
                label="Kanał rejestracji wizyty"
              >
                <AppointmentChannelSelector
                  value={visit?.appointmentChannelId || ''}
                  channels={props.appointmentChannels}
                  onChange={e => changeAppointmentChannel(e.value)}
                />
              </FormRow>
              <FormRow controlId="description" label="Opis">
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="description"
                  value={visit?.description || ''}
                  onChange={e =>
                    propertyChange({ description: e.target.value })
                  }
                />
              </FormRow>
              <FormRow controlId="startVisit" label="Początek wizyty">
                <Form.Control
                  type="datetime-local"
                  value={momencik(visit?.visitDateTime, 'YYYY-MM-DDTHH:mm')}
                  onChange={e =>
                    propertyChange({ visitDateTime: new Date(e.target.value) })
                  }
                />
              </FormRow>
              <FormRow controlId="visitTime" label="Okno czasowe wizyty (min)">
                <Form.Control
                  type="number"
                  value={visit?.visitTime || 0}
                  onChange={e =>
                    propertyChange({ visitTime: Number(e.target.value) })
                  }
                />
              </FormRow>
            </Form>
          </Card.Body>
        </Card>
        <ValidationAlert show={showSaveErrors} errors={saveErrors} />
      </Modal.Body>
      <Modal.Footer>
        {props.id && (
          <LastEditorInfo id={props.id} method={spd.visits.getLastEditor} />
        )}
        <Button variant="secondary" onClick={props.onCancel}>
          Anuluj
        </Button>
        <SpinnerButton
          variant="primary"
          isLoading={isSaving}
          onClick={onSubmit}
        >
          Zapisz
        </SpinnerButton>
      </Modal.Footer>
    </Modal>
  );
};
